import type {
  CurrentStockRow,
  FlowcountState,
  HealthStatus,
  ItemLocationSetting,
  Purchase,
  Role,
} from './types';

export function bruneiDateString(date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Brunei', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const value = Object.fromEntries(parts.filter((part) => part.type === 'year' || part.type === 'month' || part.type === 'day').map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

export const FRACTIONS = [
  { label: 'Full', fraction: 1 },
  { label: '¾', fraction: 0.75 },
  { label: '½', fraction: 0.5 },
  { label: '¼', fraction: 0.25 },
  { label: 'Very Low', fraction: 0.125 },
  { label: 'Empty', fraction: 0 },
] as const;

const QUANTITY_SCALE = 1_000_000;

/** Keep normalized inventory arithmetic stable without changing the stored quantity's scale. */
export function roundQuantity(value: number): number {
  return Math.round((value + Number.EPSILON) * QUANTITY_SCALE) / QUANTITY_SCALE;
}

export function quantityStep(unitLabel: string, fractionButtons = false): string {
  if (unitLabel === 'kg') return '0.01';
  if (fractionButtons && unitLabel === 'bottle') return '0.125';
  return '1';
}

export function formatQuantity(value: number | null, unitLabel: string): string {
  if (value === null || !Number.isFinite(value)) return '—';
  if (unitLabel === 'kg') return value.toFixed(2);
  if (unitLabel === 'bottle') return value.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

export function normalizeQuantity(
  state: Pick<FlowcountState, 'units'>,
  itemId: string,
  quantity: number,
  unitLabel: string,
): number {
  if (!Number.isFinite(quantity) || quantity < 0) throw new Error('Quantity must be a non-negative number.');
  const unit = state.units.find((candidate) => candidate.itemId === itemId && candidate.label === unitLabel && candidate.active);
  if (!unit) throw new Error(`Unit ${unitLabel} is not configured for this item.`);
  return roundQuantity(quantity * unit.multiplierToBase);
}

export function healthFor(quantity: number | null, lowThreshold: number | null): HealthStatus {
  if (quantity === null) return 'UNKNOWN';
  if (quantity <= 0) return 'OUT';
  if (lowThreshold !== null && quantity <= lowThreshold) return 'LOW';
  return 'OK';
}

export function currentStockFor(
  state: Pick<FlowcountState, 'countLines' | 'movements'>,
  setting: ItemLocationSetting,
  now = new Date(),
): CurrentStockRow {
  const lines = state.countLines
    .filter((line) => line.itemId === setting.itemId && line.locationId === setting.locationId)
    .sort((a, b) => b.recordedAt.localeCompare(a.recordedAt));
  const baseline = lines[0];
  if (!baseline) {
    return { itemId: setting.itemId, locationId: setting.locationId, quantity: null, baselineAt: null, health: 'UNKNOWN', stale: true };
  }
  const laterMovement = state.movements
    .filter((movement) => movement.itemId === setting.itemId && movement.locationId === setting.locationId && movement.recordedAt > baseline.recordedAt)
    .reduce((sum, movement) => sum + movement.normalizedDelta, 0);
  const quantity = roundQuantity(baseline.normalizedQuantity + laterMovement);
  const ageHours = (now.getTime() - new Date(baseline.recordedAt).getTime()) / 3_600_000;
  return {
    itemId: setting.itemId,
    locationId: setting.locationId,
    quantity,
    baselineAt: baseline.recordedAt,
    health: healthFor(quantity, setting.lowThreshold),
    stale: Boolean(setting.dailyCount && ageHours > 36),
  };
}

export function currentStockRows(state: FlowcountState, locationIds?: string[], now = new Date()): CurrentStockRow[] {
  return state.settings
    .filter((setting) => setting.active && setting.stocked && (!locationIds || locationIds.includes(setting.locationId)))
    .map((setting) => currentStockFor(state, setting, now));
}

export function outletHealth(rows: CurrentStockRow[]): HealthStatus {
  if (rows.length === 0 || rows.every((row) => row.health === 'UNKNOWN')) return 'UNKNOWN';
  if (rows.some((row) => row.health === 'OUT')) return 'OUT';
  if (rows.some((row) => row.health === 'LOW' || row.stale || row.health === 'UNKNOWN')) return 'LOW';
  return 'OK';
}

export function monthToDateSpend(purchases: Purchase[], year: number, monthIndex: number) {
  const totals = { INVENTORY: 0, OPEX: 0, CAPEX: 0 };
  const prefix = `${year}-${String(monthIndex + 1).padStart(2, '0')}-`;
  for (const purchase of purchases) {
    if (purchase.purchaseDate.startsWith(prefix)) totals[purchase.expenseClass] += purchase.totalAmount;
  }
  return totals;
}

export function latestPriceChange(purchases: Purchase[], itemId: string) {
  const comparable = purchases
    .flatMap((purchase) => purchase.lines.map((line) => ({ purchase, line })))
    .filter(({ line }) => line.itemId === itemId && line.normalizedQuantity && line.normalizedQuantity > 0)
    .map(({ purchase, line }) => ({
      purchaseDate: purchase.purchaseDate,
      createdAt: purchase.createdAt,
      unitPrice: line.lineAmount / (line.normalizedQuantity as number),
    }))
    .sort((a, b) => b.purchaseDate.localeCompare(a.purchaseDate) || b.createdAt.localeCompare(a.createdAt));
  if (comparable.length === 0) return null;
  const latest = comparable[0];
  const previous = comparable[1] ?? null;
  return {
    latest: latest.unitPrice,
    previous: previous?.unitPrice ?? null,
    direction: previous ? (latest.unitPrice > previous.unitPrice ? 'UP' : latest.unitPrice < previous.unitPrice ? 'DOWN' : 'SAME') : 'NEW',
  } as const;
}

export function canAccessLocation(role: Role, membershipLocations: string[], locationId: string) {
  return role === 'MANAGEMENT' || role === 'INVENTORY_OFFICER' || membershipLocations.includes(locationId);
}

export function canTransfer(role: Role) {
  return role === 'MANAGEMENT' || role === 'INVENTORY_OFFICER';
}

export function canManageSuppliers(role: Role) {
  return role === 'MANAGEMENT' || role === 'INVENTORY_OFFICER';
}
